import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Bearer token from localStorage as fallback (for cross-origin cookies issues)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("df_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Fetch a stored file (auth-protected) as a blob URL. Caller revokes when done.
async function fileBlobUrl(path) {
  const token = localStorage.getItem("df_token");
  const res = await fetch(`${API}/files/${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Open a stored file in a new browser tab.
export async function viewFile(path) {
  // Open the tab synchronously, inside the click handler, then navigate it
  // once the file is fetched — opening it only after the await is what
  // Safari/iOS treat as an untrusted popup and silently block or strand on
  // about:blank. Not passing noopener here since we need the window
  // reference back to route the blob into it; safe because the tab only
  // ever gets pointed at our own same-origin blob URL.
  const w = window.open("", "_blank");
  try {
    const url = await fileBlobUrl(path);
    if (!w) throw new Error("Please allow pop-ups to preview files");
    w.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (w) w.close();
    throw e;
  }
}

// Trigger a download of a stored file.
export async function downloadFile(path, name) {
  const url = await fileBlobUrl(path);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || path.split("/").pop();
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
